import React, { useMemo } from 'react';
import { StreamWithChatRate } from '../types';
import { getHeatRatio, getHeatTier, isHeatReady, HEAT_BASELINE_S } from '../utils/heat';
import { IconFlame } from './icons';
import './HeatRail.css';

interface HeatRailProps {
  streams: StreamWithChatRate[];
  selectedId?: string | null;
  onStreamSelect?: (stream: StreamWithChatRate) => void;
}

/** How many channels the rail can show at once. CSS drops the tail on narrow screens. */
const SLOTS = 4;
/** The 'busy' tier boundary — below it a channel is breathing, not spiking,
 *  and a rail headed "hottest now" would be lying. */
const MIN_HEAT = 2;
/** A 3-message-per-10s channel can hit 4× on noise. Require real volume. */
const MIN_RATE = 1;
/** Meter runs against the thermal ramp itself, so a chip's bar means the same
 *  thing here as the legend in the guide: full = blazing. */
const RAMP_TOP = 5;

/**
 * The scanner readout: what the chat monitor hears across every channel it
 * has joined, right now. Three honest states — connecting, calibrating (the
 * 5-minute baseline needs 45s before a ratio means anything), and the movers
 * themselves. Each mover is a jump button.
 */
export const HeatRail: React.FC<HeatRailProps> = ({ streams, selectedId, onStreamSelect }) => {
  const { movers, listening, baseline } = useMemo(() => {
    const heard = streams.filter(s => (s.sampleSeconds ?? 0) > 0);
    const found = streams
      .filter(s => isHeatReady(s) && (s.chatRate10s || 0) >= MIN_RATE)
      .map(s => ({ stream: s, heat: getHeatRatio(s) }))
      .filter(m => m.heat >= MIN_HEAT)
      .sort((a, b) => b.heat - a.heat)
      .slice(0, SLOTS);

    const oldest = heard.reduce((max, s) => Math.max(max, s.sampleSeconds ?? 0), 0);
    return {
      movers: found,
      listening: heard.length,
      baseline: Math.min(1, oldest / HEAT_BASELINE_S),
    };
  }, [streams]);

  if (listening === 0) {
    return (
      <div className="heat-rail" role="status">
        <span className="rail-eyebrow">tuning in</span>
        <span className="rail-sweep" aria-hidden>
          <span className="rail-sweep-run" />
        </span>
        <span className="rail-note">opening chat sockets</span>
      </div>
    );
  }

  if (movers.length === 0) {
    const warming = baseline < 1;
    return (
      <div className="heat-rail" role="status">
        <span className="rail-eyebrow">listening</span>
        <span className="rail-count">{listening} chats</span>
        <span className="rail-sweep" aria-hidden>
          {warming ? (
            <span className="rail-sweep-fill" style={{ width: `${Math.max(3, baseline * 100)}%` }} />
          ) : (
            <span className="rail-sweep-run" />
          )}
        </span>
        <span className="rail-note">
          {warming
            ? `baseline ${Math.round(baseline * HEAT_BASELINE_S)}s / ${HEAT_BASELINE_S}s`
            : 'nothing spiking yet'}
        </span>
      </div>
    );
  }

  return (
    <div className="heat-rail live">
      <span className="rail-eyebrow hot">hottest now</span>
      <div className="rail-movers">
        {movers.map(({ stream, heat }, i) => {
          const tier = getHeatTier(heat);
          const fill = Math.min(100, Math.round((heat / RAMP_TOP) * 100));
          return (
            <button
              key={stream.user_login}
              type="button"
              className={`rail-mover heat-${tier} ${stream.id === selectedId ? 'watching' : ''} ${i === 0 ? 'lead' : ''}`}
              onClick={() => onStreamSelect?.(stream)}
              title={`${stream.user_name} — chat is running ${heat.toFixed(1)}× its 5-minute average (${(stream.chatRate10s || 0).toFixed(1)} msgs/s). Click to watch.`}
            >
              <span className="mover-name">{stream.user_name}</span>
              <span className="mover-heat">
                <IconFlame size={9} />
                {heat.toFixed(1)}×
              </span>
              <span className="mover-track" aria-hidden>
                <span className="mover-fill" style={{ width: `${fill}%` }} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};
